import React, { useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { formatCompanyDateTime } from '../utils/companyDateFormat';

interface ClockSuccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  action: 'clockIn' | 'clockOut';
}

const ClockSuccessDialog: React.FC<ClockSuccessDialogProps> = ({ isOpen, onClose, action }) => {
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(onClose, 2500);
    return () => clearTimeout(timer);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isClockIn = action === 'clockIn';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-success-pop"
        role="dialog"
        aria-labelledby="success-title"
      >
        <div
          className={`absolute inset-0 opacity-10 ${
            isClockIn ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-gradient-to-br from-amber-400 to-orange-500'
          }`}
        />
        <div className="relative px-8 py-10 text-center">
          <div
            className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${
              isClockIn ? 'bg-emerald-100' : 'bg-amber-100'
            }`}
          >
            <CheckCircle2
              className={`h-10 w-10 ${isClockIn ? 'text-emerald-600' : 'text-amber-600'}`}
              strokeWidth={2}
            />
          </div>
          <h2 id="success-title" className="text-xl font-bold text-neutral-800 mb-1">
            {isClockIn ? 'Clock In Successful' : 'Clock Out Successful'}
          </h2>
          <p className="text-sm text-neutral-500 mb-6">
            {format(new Date(), 'EEEE')}, {formatCompanyDateTime(new Date())}
          </p>
          <button
            onClick={onClose}
            className={`w-full py-3 px-6 rounded-xl font-semibold text-white transition-colors ${
              isClockIn
                ? 'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700'
                : 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700'
            }`}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClockSuccessDialog;
